// apps/vendor/src/features/announcements/AnnouncementCarousel.tsx
//
// Vendor topbar announcement carousel — parity with merchant-v3's
// components/topbar/AnnouncementCarousel.tsx. Fed by
// useAnnouncements({ surface: 'topbar' }). Auto-rotates 5s when N > 1;
// manual Prev/Next + per-id dismiss (localStorage, unless force_show).

import { useCallback, useEffect, useState, type JSX } from 'react';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  AlertTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  MegaphoneIcon,
  SparklesIcon,
  XIcon,
} from './icons.js';
import {
  dismissAnnouncement,
  isAnnouncementDismissed,
  useAnnouncements,
  type Announcement,
  type AnnouncementCategory,
} from './api.js';

type IconComponent = (props: { className?: string }) => JSX.Element;

const CATEGORY_ICON: Record<AnnouncementCategory, IconComponent> = {
  system_notice: ClockIcon,
  promo_marketing: SparklesIcon,
  maintenance: AlertTriangleIcon,
  feature_release: MegaphoneIcon,
};

const CATEGORY_TAG_CLASSES: Record<AnnouncementCategory, string> = {
  system_notice: 'bg-red-100 text-red-700',
  promo_marketing: 'bg-sky-100 text-sky-700',
  maintenance: 'bg-red-200 text-red-900',
  feature_release: 'bg-emerald-100 text-emerald-700',
};

const CATEGORY_PILL_CLASSES: Record<AnnouncementCategory, string> = {
  system_notice: 'border-red-200 bg-red-50 text-red-900',
  promo_marketing: 'border-sky-200 bg-sky-50 text-sky-900',
  maintenance: 'border-red-300 bg-red-100 text-red-900',
  feature_release: 'border-emerald-200 bg-emerald-50 text-emerald-900',
};

function pickLocale(
  s: { en: string; vi?: string; fr?: string },
  lng: string,
): string {
  if (lng.startsWith('vi') && s.vi) return s.vi;
  if (lng.startsWith('fr') && s.fr) return s.fr;
  return s.en;
}

export function AnnouncementCarousel(): JSX.Element | null {
  const { t, i18n } = useTranslation('common');
  const { data } = useAnnouncements({ surface: 'topbar' });

  // Local dismiss tick so clicking X re-renders (localStorage source does not
  // trigger React updates; setIdx(0) alone bails out when already at 0).
  const [dismissTick, setDismissTick] = useState(0);

  // "dismissible wins" (user decision 2026-07-20): dismissible → honour close
  // state; non-dismissible → sticky. force_show is not a "can't close" flag.
  const visible = (data ?? []).filter(
    (a: Announcement) => (a.dismissible ? !isAnnouncementDismissed(a.id) : true),
  );
  void dismissTick;

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (visible.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIdx((i) => (i + 1) % visible.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [visible.length]);

  const current: Announcement | undefined = visible[idx];

  const handleDismiss = useCallback(() => {
    if (!current) return;
    dismissAnnouncement(current.id);
    setIdx(0);
    setDismissTick((n) => n + 1);
  }, [current]);

  if (!current) return null;

  const Icon = CATEGORY_ICON[current.category];
  const title = pickLocale(current.title_i18n, i18n.language);
  const body = pickLocale(current.body_i18n, i18n.language);
  const total = visible.length;

  return (
    <div
      role="status"
      data-testid="vendor-topbar-announcement-carousel"
      data-announcement-id={current.id}
      className={`inline-flex min-w-0 max-w-[640px] items-center gap-2 rounded-full border px-2 py-1 text-xs ${CATEGORY_PILL_CLASSES[current.category]}`}
    >
      <span
        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${CATEGORY_TAG_CLASSES[current.category]}`}
      >
        <Icon className="h-3 w-3" />
        {title}
      </span>
      <span className="min-w-0 truncate">{body}</span>
      {total > 1 ? (
        <span className="ml-1 inline-flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            aria-label={t('announcement.previous')}
            onClick={() => setIdx((i) => (i - 1 + total) % total)}
            /* dark-ok: translucent wash over a colour-tinted banner, correct in both modes */
            className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-black/5"
          >
            <ChevronLeftIcon className="h-3.5 w-3.5" />
          </button>
          <span className="text-[11px] font-medium tabular-nums">
            {idx + 1}/{total}
          </span>
          <button
            type="button"
            aria-label={t('announcement.next')}
            onClick={() => setIdx((i) => (i + 1) % total)}
            /* dark-ok: translucent wash over a colour-tinted banner, correct in both modes */
            className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-black/5"
          >
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        </span>
      ) : null}
      {current.dismissible ? (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('announcement.dismiss')}
          /* dark-ok: translucent wash over a colour-tinted banner, correct in both modes */
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-black/5"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export default AnnouncementCarousel;
